
import { OptionsObject } from 'notistack'
import React from 'react'
import {
    Business,
    BusinessSettings,
    LoggedInUser,
    RoleAndStaffType,
    RoleWrapper,
} from '../utils/types'
import { Component } from '../hooks/types'
import { ThemeOptions } from '@mui/material'
import { LoadableComponent } from '@loadable/component'


export type Employee = {
    id?: string
    firstName?: string
    lastName?: string
    description?: string
    profileImage: {
        largeImage?: string
        thumbImage?: string
    } | null
    metadata?: string | null
}

export type Location = {
    id?: string
    name?: string
    invoiceCurrency: {
        currency?: string
        currencySymbol?: string
    }
    preference?: {
        currency?: string
        currencySymbol?: string
        dateFormat?: string
        language?: string
        timeFormat?: string
        timezone?: string
        uiInfo?: string
        IsProductTourVisited?: boolean
        isWizardVisited?: boolean
        DashboardWidgetSetting?: string
    } | null
    slugObject?: {
        id?: string
        slugType?: string
        slugValue?: string
    } | null
    gallery?: {
        default?: {
            thumbImage?: string
            largeImage?: string
        } | null
        images?: {
            largeImage?: string
            thumbImage?: string
        }[]
    } | null
    address?: {
        country?: string
        latitude?: number
        locality?: string
        longitude?: number
        postalCode?: string
        region?: string
        streetAddress?: string
    } | null
    telephones?: string[]
    description?: string
    active?: boolean
    metadata?: Record<string, any>
    services?: {
        count?: number
        edges?: {
            node?: {
                id?: string
                active?: boolean
                title?: string
                description?: string
                categoryId?: string
            } | null
        }[]
    } | null
    userRoles?: UserRoles | null
    isPrimary?: boolean
} & Pick<Partial<Company>,'apps' | 'authToken'>

export type Company = {
    id?: string
    displayName?: string
    customCompanyId?: string
    title?: string
    profession?: string
    active?: boolean
    isActive?: boolean
    preference?: {
        currency?: string
        currencySymbol?: string
        dateFormat?: string
        language?: string
        timeFormat?: string
        timezone?: string
        uiInfo?: string
        IsProductTourVisited?: boolean
        isWizardVisited?: boolean
        DashboardWidgetSetting?: string
    } | null
    gallery?: {
        default?: {
            thumbImage?: string
            largeImage?: string
        } | null
        images?: {
            largeImage?: string
            thumbImage?: string
        }[]
    } | null
    address?: {
        country?: string
        latitude?: number
        locality?: string
        longitude?: number
        postalCode?: string
        region?: string
        streetAddress?: string
    } | null
    telephones?: string[]
    description?: string
    emails?: string[]
    apps?: {
        id?: string
        appTypeId?: string
        name?: string
        active?: boolean
        isActive?: boolean
        serviceModules?: string[]
    }[]
    slugObject?: {
        id?: string
        slugType?: string
        slugValue?: string
    } | null
    locations?: Location[] | null
    companySettings?: {
        id?: string
        navMenus?: string[]
        aliases?: string | null
        customization?: {
            disableApps?: string[] | null
            disableStaffBooking?: boolean | null
        } | null
    } | null
    metadata?: Record<string, any>
    licenseWallet?: BillingSubscriptionWallet | null
    roleLevelCustomization?: {
        disableApps?: string[] | null
        readonlyApps?: string[] | null
    } | null
    authToken?: string
    website?: string
    userRoles?: UserRoles | null
    roleLevel?: 'com' | 'loc'
    userRoleForRegistration?: UserRoleMatterSuite
    employeeDetails?: EmployeeDetails
    contactDetails?: ContactDetails
    isDefaultData?: boolean
    taxId?: string
}
export interface EmployeeDetails {
    firstName?: string
    lastName?: string
    email?: string
    gender?: number
    employeeNumber?: string
    phoneNumber?: string
    departmentId?: string
    designationId?: string
    departmentTitle?: string
    designationName?: string
    hireDate?: string
    userId?: string
    isInvitationAccepted?: boolean
    profileIds?: string[]
    timeZone?: string | null
    hasExtendedProfiles?: boolean
    displayOrder?: number
    disableLogin?: boolean
    profileImageThumbImage?: string
    profileImageLargeImage?: string
    fieldVariableMask?: string
    changedSettings?: any[]
    language?: string | null
    displayLocations?: DisplayLocations[]
    userRoleList?: UserRoleMatterSuite[]
    roleName?: string | null
    preference?: EmployeePreference[] | null
    id?: string
    parent?: string
    isActive?: boolean
    metadata?: Record<string, any>
    isDeleted?: boolean
    deletedBy?: string
    deletedOn?: string
    updatedBy?: string
    updatedOn?: string
    createdBy?: string
    createdOn?: string
}
export interface ContactDetails {
    contactType?: number
    companyName?: string
    companyId?: string
    contactPrefix?: string
    initial?: string
    firstName?: string
    middleName?: string
    lastName?: string
    employee?: string
    primaryEmail?: string
    email?: Email[]
    phone?: Phone[]
    website?: Website[]
    address?: Address[]
    timezone?: string
    oldCustomFieldSetAssociation?: string
    profileImageThumbImage?: string
    profileImageLargeImage?: string
    fieldVariableMask?: null
    customFieldSetAssociation?: any[]
    primaryPhone?: string
    isClient?: boolean
    title?: string
    userId?: string
    isExternalCounsel?: boolean
    id?: string
    parent?: string
    isActive?: boolean
    metadata?: string
    isDeleted?: boolean
    deletedBy?: string
    deletedOn?: string
    updatedBy?: null
    updatedOn?: string
    createdBy?: string
    createdOn?: string
}
export interface Address {
    country?: string
    latitude?: number
    locality?: string
    longitude?: number
    postalCode?: string
    region?: string
    streetAddress?: string
    countryCode?: null
    addressType?: number
}

export interface Email {
    email?: string
    emailType?: number
    isPrimary?: boolean
}

export interface Phone {
    phone?: string
    phoneType?: number
    isPrimary?: boolean
}
export interface Website {
    website?: string
    websiteType?: number
    isPrimary?: boolean
}

export interface DisplayLocations {
    addressCountry?: string
    addressLatitude?: number
    addressLocality?: string
    addressLongitude?: number
    addressPostalCode?: string
    addressRegion?: string
    addressStreetAddress?: string
    description?: string
    gallery?: {
        default?: {
            thumbImage?: string
            largeImage?: string
        } | null
        images?: {
            largeImage?: string
            thumbImage?: string
        }[]
    }
    name?: string
    preference?: Preference
    slugObject?: SlugObject
    telephones?: string[]
    id?: string
    parent?: string
    isActive?: boolean
    metadata?: Record<string, any>
    isDeleted?: boolean
    userRoles?: UserRoleMatterSuite
    deletedBy?: string
    deletedOn?: string
    updatedBy?: string
    updatedOn?: string
    createdBy?: string
    createdOn?: string
}
export interface SlugObject {
    appTypeId?: string
    name?: string
    serviceModules?: string[]
    id?: string
    parent?: null | string
    isActive?: boolean
    metadata?: null
    isDeleted?: null
    deletedBy?: null
    deletedOn?: null
    updatedBy?: null
    updatedOn?: Date | null
    createdBy?: null
    createdOn?: Date
    navMenus?: string
    licenseName?: string
    slugValue?: string
    slugType?: string
}
export interface EmployeePreference {
    id?: string
    color?: string
    title?: string
    selected?: boolean
}
export interface UserRoleMatterSuite {
    userId?: string
    roleId?: string
    locationId?: null
    employeeId?: null
    roleName?: string
    roleLevel?: string
    sysName?: string
    id?: string
    parent?: string
    isActive?: boolean
    metadata?: null
    isDeleted?: boolean
    deletedBy?: string
    deletedOn?: string
    updatedBy?: string
    updatedOn?: string
    createdBy?: string
    createdOn?: string
}
export type Group = {
    companies?: Company[]
    id?: string
    name?: string
    groupSettings?: {
        id?: string
        adminCss?: string
        hasExtendedFields?: boolean
    } | null
}
export enum AdminRole {
    Owner = 'sys_owner',
    CoOwner = 'sys_co_owner',
    Client = 'sys_client',
}
export type ModuleRole = {
    apps?: {
        id?: string
        appTypeId?: string
        name?: string
        active?: boolean
        isActive?: boolean
        serviceModules?: string[]
    }[]
    authToken?:string
    levelId?: string
    roleId?: string
    levelDetails?: {
        id?: string
    } | null
    role?: {
        id: string
        roleName: string
        sysName: string
        isDefault: boolean
        priority: number
        level: string
        moduleRoles: { name: string }[]
    } | null
}

export type UserRoles = {
    role?: ModuleRole[] | null
}

export type Preference = {
    dateFormat?: string | null
    language?: string | null
    timeFormat?: string | null
    timezone?: string | null
    currency?: string | null
    currencySymbol?:string | null
    uiInfo?: string | null
    startOfWeek?: number
    IsProductTourVisited?: boolean
    isWizardVisited?: boolean
    DashboardWidgetSetting?: string
}

export type Parents = {
    groupId?: string
    companyId?: string
    locationId?: string
    userId?: string
}

export type ModuleRestrictionTypes =
    | 'ATTRIBUTE'
    | 'CYCLIC_NUMBER_LIMIT'
    | 'FEATURE_RPC'
    | 'MODULE'
    | 'NUMBER_LIMIT'
export type ModuleLicense = {
    enabled: boolean
    freeLimit: number
    licenseLimit: number
    maxAddonLimit: number
    maxLimit: number
    moduleName: string
    paidAddonLimit: number
    restrictionSlugs: ReadonlyArray<string>
    restrictionType: ModuleRestrictionTypes
    rpcs: ReadonlyArray<string>
    slug: string
    totalLimit: number
}
export type License = {
    moduleLicenses: ModuleLicense[]
}
export type LicenseWallet = {
    license?: License | null
}

export type Viewer = {
    companies?: Company[]
    groups?: Group[]
    id?: string
    firstName?: string
    lastName?: string
    email?: string
    profileImage?: {
        thumbImage?: string
        largeImage?: string
    } | null
    preferences?: {
        dateFormat?: string
        language?: string
        timeFormat?: string
        timezone?: string
        uiInfo?: string
        startOfWeek?: number
        UIType?: string
        currency?: string
        currencySymbol?:string
        IsProductTourVisited?: boolean
        isWizardVisited?: boolean
        DashboardWidgetSetting?: string
        isNotificationsEnabled?: boolean
    } | null
    userRoles?: UserRoles | null
    metadata?: string | null
    currency?: string
    currencySymbol?: string
    currencyFormat?: string
    country?: string
    refreshToken?: string
    phoneNumber?: string
    passwordChangedOn?: string
    twoFactorAuth?: boolean
    birthDate?: string
    verifiedTelephones?: string[]
    telephones?: string[]
}

export type Mutable<T> = { -readonly [P in keyof T]: Mutable<T[P]> }

export interface SwitcherProps {
    value?: string
    onChange?: (e: string) => void
    //all works only with controlled switcher
    all?: boolean
    //override locations from config
    locations?: Location[]
}

export interface TemplateProps extends ConfigProviderProps {
    mobileView: any
}
export interface AppBarItems {
    key: string
    component: Component
}
export interface TemplateLoaderProps {
    children?: React.ReactNode
    isAmp?: boolean
    primaryColor?: string
    darkMode?: boolean
    viewer?: Viewer | null
    refetchViewer?: () => Promise<Viewer | null | undefined | void>
    preference?: Preference | null
    admin?: boolean
    employeeIds?: string[] | readonly string[] | null
    groups?: Group[] | null
    companies?: Company[] | null
    locations?: Location[] | null
    employee?: Employee | null
    template?: React.ComponentType<TemplateProps> | LoadableComponent<TemplateProps>
    templates?: {
        component: React.ComponentType<TemplateProps> | LoadableComponent<TemplateProps>
        canActivate: () => boolean
    }[]
    baseUrl?: string
    logoUrl?: string
    groupId?: string
    companyId?: string
    locationId?: string
    settings?: any
    appBar?: React.ReactElement<any, any>
    appBarConfig?: {
        leftItems?: Array<AppBarItems>
        centerItem?: Array<AppBarItems>
        rightItems?: Array<AppBarItems>
    }
    roleWrapper?: RoleWrapper
    business?: Business
    loggedInUser?: LoggedInUser
    businessSettings?: BusinessSettings
    roleAndStaffType?: RoleAndStaffType
    supportedLanguages?: any
    subscriptionInfo?: SubscriptionInfo
    navbarPosition?: 'Horizontal' | 'Vertical'
    horizontalNavbarStyles?: React.CSSProperties
}
export interface SubscriptionInfo {
    currentMembership?: {
        ActiveUsers?: number
        CurrentPlan?: string
    }
    currentSubscription?: {
        ActiveUsers?: number
        CurrentPlan?: string
    }
    planInfo?: SubscriptionInfoNew
    isPlanActive?: boolean
}
export interface SubscriptionInfoNew {
    PlanId?: number
    IsNewPricingPlan?: number
    IsYearly?: number
    TotalActiveUsers?: number
    PrimarySubscriptionAmount?: number
    PrimarySubscriptionPaidTill?: string
    AdditionalUserSubscriptionPaidTill?: string
    StorageSubscriptionPaidTill?: string
    SMSSubscriptionPaidTill?: string
    AdditionalUsersAmountPerMonth?: number
    IsSMSBillingSubscribed?: number
    IsStorageSubscribed?: number
    StorageSubscriptionAmount?: number
    IsStorageSubscriptionYearly?: number
    IsPrimarySubscriptionOverDue?: number
    IsAdditionalUserSubscriptionOverDue?: number
    MonthlyChargesPerUser?: number
    YearlyChargesPerUser?: number
}
export type UserRole = {
    parent: string
    name: string
    sysName?: string
    level?:string
}

export interface Config {
    viewer?: Viewer | null
    userRoles?: UserRole[]
    roleId?:string
    isRoleLevelCom?: boolean
    groups?: Group[]
    group?: Group
    groupId?: string
    companies?: Company[]
    company?: Company
    companyId?: string
    locations?: Location[]
    activeLocations?:Location[] | null
    location?: Location
    employee?: Employee | null
    locationId?: string
    activeApps?: string[]
    baseUrl?: string
    logoUrl?: string
    employeeId?: string
    isEmployee?: boolean
    customerId?: string
    readonlyApps?: string[]
    disableHooks?: Record<string, Array<string>>
    staticAliases?: Record<string, Alias>
    theme?: ThemeOptions
    settings?: any
    setViewer?: any
    supportedLanguages?: any
    // currencySymbol?: string | null
    isCasefoxAdmin?: boolean
    isMattersuiteAdmin?: boolean
    isMattersuiteCoAdmin?: boolean
    isMattersuiteClient?: boolean
    isMattersuiteCoCounsel?:boolean
    isCasefoxPermissionAllowed?: (appName: Array<string>) => Record<string, boolean>
    appBar?: React.ReactElement<any, any>
    appBarConfig?: {
        leftItems?: Array<AppBarItems>
        centerItem?: Array<AppBarItems>
        rightItems?: Array<AppBarItems>
    }
    roleWrapper?: RoleWrapper
    business?: Business
    loggedInUser?: LoggedInUser
    businessSettings?: BusinessSettings
    roleAndStaffType?: RoleAndStaffType
    roleBasedObj?: RoleBasedObj
    subscriptionInfo?: SubscriptionInfo
    country?: string
    navbarPosition?: 'Horizontal' | 'Vertical'
    horizontalNavbarStyles?: any
    isSubscriptionActive?: boolean
    isActive?: boolean
    activePackages?: Addon[]
    deviceId?: string,
    currency?:string
    currencySymbol?:string,
    language?:string,
    timezone?:string,
    timeFormat?:string,
    dateFormat?:string
}

export interface ConfigProviderProps extends Config {
    children?: React.ReactNode
    setConfig: (conf: Partial<Config>) => void
    refetchViewer: () => Promise<Viewer | null | undefined | void>
}

export interface AlertProps {
    message: React.ReactNode
    options?: OptionsObject
}

export interface Alias {
    plural: string
    singular: string
}
export interface RoleBasedObj {
    roleType: number
    roleId: number
    businessId: number
    roleWrapper: RoleWrapper
}
export interface BillingSubscriptionWallet {
    addons?: Addon[]
    membershipId?: string
    couponCode?: string
    plan?: Plan
    membership?: Membership
    isSubscriptionActive?: boolean
    gatewayType?: number
    externalSubscriptionId?: null
    subscriptionType?: number
    planId?: string
    startDate?: string
    endDate?: string
    gracePeriodUpto?: string
    id?: string
    parent?: string
    isActive?: boolean
    employeeLimit?: number
    locationLimit?: number
}
export enum LimitType {
    Numeric = 0,
    Rolling = 1,
    Feature_Based = 2,
    Attribute_Based = 3,
}
export type AddonSysName = 'locations' | 'employees' | 'law_suite' | 'billing'
export interface Addon {
    planId?: string
    addonId?: string
    limitType?: LimitType
    defaultValue?: number
    priceAmount?: number
    priceCurrency?: string
    minValue?: number
    maxValue?: number
    priceId?: null
    purchasedValue?: number
    description?: string
    title?: string
    sysName?: AddonSysName
    id?: string
    parent?: string
    isActive?: boolean
}

export interface Membership {
    sysName?: string
    title?: string
    description?: null
    externalProductId?: null
    billingPlans?: null
    id?: string
    parent?: null
    isActive?: boolean
}

export interface Plan {
    title?: string
    description?: string
    priceAmount?: number
    priceCurrency?: string
    validityInDays?: number
    planType?: number
    externalPlanId?: null
    billingAddons?: null
    id?: string
    parent?: null
    isActive?: boolean
}
